/**
 * NotificationBell — board notifications bell for the app navbar.
 * Shows an unread badge, a dropdown of recent notifications, and listens for
 * real-time `board:notification` pushes over Socket.IO (toast + live refresh).
 * Self-contained (Tailwind classes) so it works in the app shell.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';
import { useAppStore } from '@/app/store';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead, useDeleteNotification, useClearAllNotifications, NOTIF_KEY } from './api';
import type { BoardNotification } from './types';

const timeAgo = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
};

export const NotificationBell: React.FC = () => {
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const deleteNotif = useDeleteNotification();
  const clearAll = useClearAllNotifications();
  const qc = useQueryClient();
  const { socket } = useSocket();
  const addNotification = useAppStore(s => s.addNotification);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Open the board (and row) a notification points to.
  const openNotif = (n: BoardNotification) => {
    if (!n.is_read) markRead.mutate(n.id);
    setOpen(false);
    if (!n.board_id) return;
    const base = location.pathname.startsWith('/bureau') ? '/bureau' : '/admin';
    const qs = new URLSearchParams({ board: n.board_id });
    if (n.row_id) qs.set('row', n.row_id);
    navigate(`${base}/boards?${qs.toString()}`);
  };

  const items = data?.items || [];
  const unread = data?.unread || 0;

  // Real-time push: refresh list + show a toast when a notification arrives.
  useEffect(() => {
    if (!socket) return;
    const onNotif = (n: BoardNotification) => {
      qc.invalidateQueries({ queryKey: NOTIF_KEY });
      addNotification({ type: 'info', title: n.title, message: n.body || undefined, link: '/admin/boards' });
    };
    socket.on('board:notification', onNotif);
    return () => { socket.off('board:notification', onNotif); };
  }, [socket, qc, addNotification]);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="relative flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        title="Notifications"
        onClick={() => setOpen(o => !o)}
      >
        <BellIcon className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-2 w-80 max-h-[60vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-2xl z-[1000]">
          <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-gray-100 sticky top-0 bg-white z-10">
            <span className="text-sm font-bold text-gray-800">Notifications</span>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button className="text-xs font-semibold text-indigo-600 hover:underline" onClick={() => markAll.mutate()}>
                  Tout marquer lu
                </button>
              )}
              {items.length > 0 && (
                <button className="text-xs font-semibold text-gray-500 hover:text-red-600 hover:underline" onClick={() => clearAll.mutate()}>
                  Tout effacer
                </button>
              )}
            </div>
          </div>
          {items.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-gray-400">Aucune notification</div>
          )}
          {items.map(n => (
            <div
              key={n.id}
              role="button"
              tabIndex={0}
              className={`group relative w-full text-left px-3 py-2.5 pr-8 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer ${n.is_read ? 'opacity-70' : ''}`}
              onClick={() => openNotif(n)}
              onKeyDown={e => { if (e.key === 'Enter') openNotif(n); }}
            >
              <div className="flex items-start gap-2">
                {!n.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />}
                <div className={n.is_read ? 'pl-4' : ''}>
                  <div className="text-[13px] font-semibold text-gray-800">{n.title}</div>
                  {n.body && <div className="text-xs text-gray-500 mt-0.5 leading-snug">{n.body}</div>}
                  <div className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</div>
                </div>
              </div>
              <button
                type="button"
                className="absolute top-2 right-2 w-5 h-5 grid place-items-center rounded text-gray-300 hover:text-red-500 hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition"
                title="Effacer"
                onClick={e => { e.stopPropagation(); deleteNotif.mutate(n.id); }}
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
